import React from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Lock, User, Eye, EyeOff, ArrowRight, Loader2, Sparkles } from "lucide-react";
import { useCms } from "@/lib/cms";

export function Login() {
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const redirect = params.get("redirect") || "";
  const c = useCms("auth");
  const [busy, setBusy] = React.useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      if (redirect) nav(redirect);
      else if (u.role && u.role !== "customer") nav("/admin");
      else nav("/account");
    } catch { /* toasted */ } finally { setBusy(false); }
  };
  return (
    <AuthShell title={c.login_title} subtitle={c.login_subtitle} cms={c}>
      <form onSubmit={submit} className="space-y-4">
        <Input label="Email" value={email} onChange={setEmail} type="email" testid="login-email" icon={Mail} autoComplete="email"/>
        <Input label="Password" value={password} onChange={setPassword} type="password" testid="login-password" icon={Lock} autoComplete="current-password"/>
        <div className="flex justify-end -mt-1">
          <Link to="/forgot-password" className="text-xs text-[#0E4D34] hover:underline" data-testid="link-forgot">Forgot password?</Link>
        </div>
        <SubmitButton busy={busy} testid="btn-login-submit">Sign In</SubmitButton>
      </form>
      <Divider/>
      <div className="text-sm text-center text-neutral-600">New to Avishka Navya? <Link to={`/register${redirect ? `?redirect=${encodeURIComponent(redirect)}` : ""}`} className="text-[#991B1B] font-semibold hover:underline" data-testid="link-register">Create an account</Link></div>
    </AuthShell>
  );
}

export function Register() {
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [name, setName] = React.useState("");
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const redirect = params.get("redirect") || "";
  const c = useCms("auth");
  const [busy, setBusy] = React.useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await register(email, password, name); nav(redirect || "/account"); } catch { /* toasted */ } finally { setBusy(false); }
  };
  const strength = password.length === 0 ? 0 : password.length < 6 ? 1 : password.length < 10 || !/\d/.test(password) ? 2 : 3;
  return (
    <AuthShell title={c.register_title} subtitle={c.register_subtitle} cms={c}>
      <form onSubmit={submit} className="space-y-4">
        <Input label="Full Name" value={name} onChange={setName} testid="reg-name" icon={User} autoComplete="name"/>
        <Input label="Email" value={email} onChange={setEmail} type="email" testid="reg-email" icon={Mail} autoComplete="email"/>
        <div>
          <Input label="Password" value={password} onChange={setPassword} type="password" testid="reg-password" icon={Lock} autoComplete="new-password"/>
          <div className="mt-2 flex gap-1.5" aria-hidden="true">
            {[1, 2, 3].map((n) => (
              <span key={n} className={`h-1 flex-1 rounded-full transition-colors duration-300 ${strength >= n ? (strength === 1 ? "bg-[#991B1B]" : strength === 2 ? "bg-[#D4AF37]" : "bg-[#0E4D34]") : "bg-neutral-200"}`}/>
            ))}
          </div>
          <div className="text-[11px] text-neutral-500 mt-1">{strength === 0 ? "Use at least 6 characters" : strength === 1 ? "Too short" : strength === 2 ? "Good — add numbers for a stronger password" : "Strong password"}</div>
        </div>
        <SubmitButton busy={busy} testid="btn-register-submit">Create Account</SubmitButton>
      </form>
      <Divider/>
      <div className="text-sm text-center text-neutral-600">Already have an account? <Link to={`/login${redirect ? `?redirect=${encodeURIComponent(redirect)}` : ""}`} className="text-[#0E4D34] font-semibold hover:underline">Sign in</Link></div>
    </AuthShell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = React.useState("");
  const [sent, setSent] = React.useState(false);
  const submit = async (e) => {
    e.preventDefault();
    await api.post("/auth/forgot-password", { email });
    setSent(true);
  };
  return (
    <AuthShell title="Reset your Password" subtitle="We'll email you a link to set a new password">
      {sent ? <p className="text-sm text-center text-neutral-600">If that email is registered, a reset link has been sent.</p> :
        <form onSubmit={submit} className="space-y-4">
          <Input label="Email" value={email} onChange={setEmail} type="email" testid="forgot-email" icon={Mail}/>
          <SubmitButton testid="btn-forgot-submit">Send Reset Link</SubmitButton>
        </form>
      }
      <div className="mt-6 text-sm text-center"><Link to="/login" className="text-[#0E4D34] hover:underline">Back to sign in</Link></div>
    </AuthShell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = React.useState("");
  const nav = useNavigate();
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/reset-password", { token, password: pw }); toast.success("Password reset. Please sign in."); nav("/login"); }
    catch (err) { toast.error(err.response?.data?.detail || "Invalid or expired link"); }
  };
  return (
    <AuthShell title="Set a New Password">
      <form onSubmit={submit} className="space-y-4">
        <Input label="New Password" value={pw} onChange={setPw} type="password" testid="reset-password" icon={Lock} autoComplete="new-password"/>
        <SubmitButton testid="btn-reset-submit">Update Password</SubmitButton>
      </form>
    </AuthShell>
  );
}

/* ---------- shared look for Sign in / Create account / password pages ---------- */
function RotatingQuote({ quotes }) {
  const list = (quotes || []).filter(Boolean);
  const [i, setI] = React.useState(0);
  React.useEffect(() => {
    if (list.length < 2) return;
    const t = setInterval(() => setI((x) => (x + 1) % list.length), 3800);
    return () => clearInterval(t);
  }, [list.length]);
  if (!list.length) return null;
  return (
    <div className="relative h-[4.5rem] sm:h-20 overflow-hidden">
      <AnimatePresence mode="wait">
        <motion.p key={i} initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -18 }} transition={{ duration: 0.5 }}
                  className="absolute inset-0 font-serif text-2xl sm:text-3xl leading-snug">
          "{list[i % list.length]}"
        </motion.p>
      </AnimatePresence>
    </div>
  );
}

function ImageColumn({ images, className }) {
  const list = [...images, ...images]; // doubled so the scroll loops seamlessly
  return (
    <div className={`flex flex-col gap-4 ${className}`}>
      {list.map((src, i) => (
        <div key={i} className="rounded-2xl overflow-hidden aspect-[3/4] shadow-2xl ring-1 ring-white/10">
          <img src={fileUrl(src)} alt="" className="w-full h-full object-cover" loading="lazy"/>
        </div>
      ))}
    </div>
  );
}

function AuthShell({ title, subtitle, children, cms }) {
  const fallback = useCms("auth");
  const c = cms || fallback;
  const imgs = (c.images || []).filter(Boolean);
  const half = Math.ceil(imgs.length / 2);
  const colA = imgs.slice(0, half);
  const colB = imgs.slice(half).length ? imgs.slice(half) : colA;
  return (
    <div className="relative min-h-[calc(100vh-120px)] grid lg:grid-cols-2 bg-[#FAF6EE] overflow-hidden" data-testid="auth-shell">
      {/* LEFT — moving saree pictures, floating shapes and rotating quotes (desktop) */}
      <div className="relative hidden lg:block overflow-hidden bg-gradient-to-br from-[#0A3926] via-[#0E4D34] to-[#1D6B4C] text-white">
        {imgs.length > 0 && (
          <div className="absolute -right-10 -top-10 bottom-0 w-[70%] flex gap-4 rotate-[8deg] opacity-60">
            <ImageColumn images={colA} className="w-1/2 auth-col-up"/>
            <ImageColumn images={colB} className="w-1/2 auth-col-down"/>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A3926] via-[#0E4D34]/85 to-transparent"/>
        <div className="auth-blob absolute -top-20 -left-16 w-80 h-80 rounded-full bg-[#D4AF37]/25 blur-3xl"/>
        <div className="auth-blob-2 absolute bottom-0 left-1/3 w-72 h-72 rounded-full bg-emerald-300/20 blur-3xl"/>
        <svg className="auth-spin absolute -bottom-28 -left-28 w-80 h-80 text-[#D4AF37]/40" viewBox="0 0 200 200" fill="none" aria-hidden="true">
          <circle cx="100" cy="100" r="96" stroke="currentColor" strokeDasharray="2 6"/>
          <circle cx="100" cy="100" r="76" stroke="currentColor" strokeDasharray="12 8"/>
          <circle cx="100" cy="100" r="56" stroke="currentColor"/>
          {Array.from({ length: 12 }).map((_, k) => (
            <ellipse key={k} cx="100" cy="44" rx="6" ry="14" stroke="currentColor" transform={`rotate(${k * 30} 100 100)`}/>
          ))}
        </svg>
        <div className="relative z-10 h-full flex flex-col justify-between p-12 xl:p-16">
          <Link to="/" className="flex items-center gap-3">
            <img src={`${process.env.PUBLIC_URL || ""}/logo.jpg`} alt="" className="w-12 h-12 rounded-full object-cover ring-2 ring-white/30"/>
            <span className="text-sm font-semibold tracking-[0.25em] uppercase">Avishka Navya</span>
          </Link>
          <div className="max-w-sm">
            <Sparkles className="text-[#D4AF37] mb-4" size={26}/>
            <RotatingQuote quotes={c.quotes}/>
            <p className="mt-6 text-sm opacity-75 leading-relaxed">Save the sarees you love, track your orders and check out faster.</p>
          </div>
          <div className="text-[11px] uppercase tracking-[0.3em] opacity-60">Handloom · Heritage · Since 1978</div>
        </div>
      </div>

      {/* RIGHT — the form */}
      <div className="relative flex flex-col items-center justify-center px-4 py-10 sm:py-16">
        <div className="auth-blob absolute top-10 right-6 w-56 h-56 rounded-full bg-[#D4AF37]/20 blur-3xl pointer-events-none"/>
        <div className="auth-blob-2 absolute bottom-10 left-0 w-64 h-64 rounded-full bg-[#0E4D34]/15 blur-3xl pointer-events-none"/>
        {/* phones: a small moving banner instead of the picture panel */}
        <div className="lg:hidden w-full max-w-md mb-6 relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0A3926] to-[#1D6B4C] text-white p-6">
          <div className="auth-blob absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#D4AF37]/30 blur-2xl"/>
          <svg className="auth-spin absolute -bottom-16 -right-16 w-44 h-44 text-[#D4AF37]/40" viewBox="0 0 200 200" fill="none" aria-hidden="true">
            <circle cx="100" cy="100" r="96" stroke="currentColor" strokeDasharray="2 6"/><circle cx="100" cy="100" r="70" stroke="currentColor" strokeDasharray="12 8"/>
          </svg>
          <div className="relative text-sm"><RotatingQuote quotes={c.quotes}/></div>
        </div>
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
                    className="relative w-full max-w-md bg-white/85 backdrop-blur-xl rounded-3xl border border-white shadow-[0_30px_80px_-30px_rgba(14,77,52,.45)] p-7 sm:p-10">
          <div className="text-center mb-7">
            <motion.img initial={{ scale: 0.6, rotate: -20, opacity: 0 }} animate={{ scale: 1, rotate: 0, opacity: 1 }} transition={{ type: "spring", stiffness: 140, damping: 12 }}
                        src={`${process.env.PUBLIC_URL || ""}/logo.jpg`} alt="Avishka Navya Collections" className="w-16 h-16 rounded-full object-cover mx-auto mb-4 ring-4 ring-[#E8F3EE]"/>
            <h1 className="font-serif text-3xl sm:text-4xl text-neutral-900 mb-2">{title}</h1>
            {subtitle && <p className="text-neutral-500 text-sm">{subtitle}</p>}
          </div>
          {children}
        </motion.div>
      </div>
    </div>
  );
}

function Divider() {
  return (
    <div className="my-6 flex items-center gap-3 text-[11px] uppercase tracking-[0.25em] text-neutral-400">
      <span className="h-px flex-1 bg-neutral-200"/>or<span className="h-px flex-1 bg-neutral-200"/>
    </div>
  );
}

function SubmitButton({ busy, children, testid }) {
  return (
    <button type="submit" disabled={busy} data-testid={testid}
            className="group relative w-full overflow-hidden rounded-full bg-[#0E4D34] hover:bg-[#0A3926] text-white py-3.5 text-xs font-semibold uppercase tracking-[0.2em] transition-colors disabled:opacity-70 flex items-center justify-center gap-2">
      <span className="absolute inset-y-0 -left-1/2 w-1/2 bg-gradient-to-r from-transparent via-white/25 to-transparent skew-x-[-20deg] translate-x-0 group-hover:translate-x-[300%] transition-transform duration-1000"/>
      {busy ? <Loader2 size={16} className="animate-spin"/> : <>{children}<ArrowRight size={14} className="group-hover:translate-x-1 transition-transform"/></>}
    </button>
  );
}

function Input({ label, value, onChange, type = "text", testid, icon: Icon, autoComplete }) {
  const [show, setShow] = React.useState(false);
  const isPw = type === "password";
  return (
    <div>
      <label className="text-[11px] font-semibold uppercase tracking-[0.18em] text-neutral-600 block mb-1.5">{label}</label>
      <div className="relative group">
        {Icon && <Icon size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400 group-focus-within:text-[#0E4D34] transition-colors"/>}
        <input required type={isPw && show ? "text" : type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} autoComplete={autoComplete}
               className={`w-full rounded-full border border-neutral-200 bg-white/90 py-3 ${Icon ? "pl-11" : "pl-4"} ${isPw ? "pr-12" : "pr-4"} text-sm focus:outline-none focus:border-[#0E4D34] focus:ring-4 focus:ring-[#0E4D34]/10 transition-all`}/>
        {isPw && (
          <button type="button" onClick={() => setShow(!show)} aria-label={show ? "Hide password" : "Show password"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-neutral-400 hover:text-[#0E4D34]">
            {show ? <EyeOff size={16}/> : <Eye size={16}/>}
          </button>
        )}
      </div>
    </div>
  );
}
